import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  artAendern,
  artAnlegen,
  arten as ladeArten,
  type Kostentraegerart,
} from '../api/bgzuschuss';
import { ApiFehler } from '../api/fehler';
import { Thema } from '../komponenten/Einstellungen';
import { Meldung } from '../komponenten/Meldung';

/**
 * Die Werteliste „Art des Kostenträgers" (E-74, PRD-38, CRM-163).
 *
 * Ein Kostenträger bezuschusst ein Training — eine Berufsgenossenschaft, ein
 * anderes Fahrsicherheitszentrum, ein Dienstleister. Welche Arten es gibt,
 * steht nicht im Programm, sondern hier (E-74: nichts fest eingebaut).
 *
 * DER SCHLÜSSEL BLEIBT, DIE BEZEICHNUNG NICHT. Der Schlüssel steht an jedem
 * Kunden, der die Art trägt; umbenannt wird nur die Bezeichnung. Er wird beim
 * Anlegen aus der Bezeichnung vorgeschlagen und lässt sich dort noch ändern,
 * danach nie wieder — wie in der Auswahllistenpflege der Honorartabelle.
 *
 * GELÖSCHT WIRD NICHT. „Außer Gebrauch" heißt: in der Kundenakte nicht mehr
 * angeboten, an den Kunden, die sie tragen, weiter lesbar.
 */

/** Was der Server als Schlüssel annimmt (Controller, `artSchluessel`). */
const SCHLUESSELMUSTER = /^[a-z][a-z0-9_]{1,39}$/;

/**
 * E-74 (CRM-163): der Schlüssel aus der Bezeichnung — klein, Umlaute
 * ausgeschrieben, alles andere als Unterstrich, höchstens 40 Zeichen.
 * „Berufsgenossenschaft (Süd)" → `berufsgenossenschaft_sued`.
 */
export function schluesselAus(bezeichnung: string): string {
  return bezeichnung
    .toLocaleLowerCase('de-DE')
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40)
    .replace(/_+$/, '');
}

type Neu = {
  readonly bezeichnung: string;
  readonly schluessel: string;
  /** Hat jemand den Schlüssel selbst geändert, folgt er der Bezeichnung nicht mehr. */
  readonly schluesselGetippt: boolean;
};

type Bearbeitung = {
  readonly schluessel: string;
  readonly bezeichnung: string;
  readonly reihenfolge: string;
};

/** Eine Reihenfolge als ganze Zahl von 0 bis 9999 — sonst null. */
function alsReihenfolge(text: string): number | null {
  return /^\d{1,4}$/.test(text.trim()) ? Number(text.trim()) : null;
}

export function Kostentraegerarten({
  darfKonfigurieren,
}: {
  readonly darfKonfigurieren: boolean;
}): JSX.Element {
  const [zeilen, setzeZeilen] = useState<readonly Kostentraegerart[]>([]);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [meldung, setzeMeldung] = useState<string | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);
  const [neu, setzeNeu] = useState<Neu | null>(null);
  const [bearbeitung, setzeBearbeitung] = useState<Bearbeitung | null>(null);

  const holen = useCallback(async () => {
    try {
      setzeZeilen(await ladeArten());
      setzeFehler(null);
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    }
  }, []);

  useEffect(() => {
    void holen();
  }, [holen]);

  /** Ein Handgriff: ausführen, neu laden, melden — ein Fehler bleibt über der Liste stehen. */
  async function handgriff(tun: () => Promise<void>, sagen: string): Promise<void> {
    setzeMeldung(null);
    setzeLaeuft(true);
    try {
      await tun();
      await holen();
      setzeMeldung(sagen);
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaeuft(false);
    }
  }

  const inGebrauch = zeilen.filter((z) => z.aktiv).length;
  const neuSchluesselFalsch = neu !== null && neu.schluessel !== '' && !SCHLUESSELMUSTER.test(neu.schluessel);
  const bearbeitungReihenfolge = bearbeitung === null ? null : alsReihenfolge(bearbeitung.reihenfolge);

  return (
    <Thema
      titel="Arten von Kostenträgern"
      zusatz={`${String(inGebrauch)} in Gebrauch${zeilen.length > inGebrauch ? `, ${String(zeilen.length - inGebrauch)} außer Gebrauch` : ''}`}
      breit
    >
      <p className="hilfe">
        Die Auswahl „Kostenträger" in der Kundenakte — etwa Berufsgenossenschaft, anderes
        Fahrsicherheitszentrum, Dienstleister. Eine Art außer Gebrauch wird nicht mehr angeboten;
        Kunden, die sie tragen, behalten sie.
      </p>
      {fehler !== null && <Meldung fehler={fehler} />}
      {meldung !== null && (
        <p className="hinweiszeile" role="status">
          {meldung}
        </p>
      )}

      <div className="tabellenhuelle tabellenhuelle--telefon">
        <table className="liste__tabelle" aria-label="Arten von Kostenträgern">
          <thead>
            <tr>
              <th scope="col">Bezeichnung</th>
              <th scope="col">Schlüssel</th>
              <th scope="col" className="zahl">
                Reihenfolge
              </th>
              <th scope="col">Stand</th>
              {darfKonfigurieren && <th scope="col">Handlung</th>}
            </tr>
          </thead>
          <tbody>
            {zeilen.map((z) => {
              const hier = bearbeitung?.schluessel === z.schluessel ? bearbeitung : null;
              return (
                <tr key={z.schluessel} className={z.aktiv ? undefined : 'liste__zeile--blass'}>
                  <th scope="row">
                    {hier === null ? (
                      z.bezeichnung
                    ) : (
                      <input
                        className="feld__eingabe"
                        aria-label={`Bezeichnung von ${z.bezeichnung}`}
                        value={hier.bezeichnung}
                        maxLength={80}
                        onChange={(e) => setzeBearbeitung({ ...hier, bezeichnung: e.target.value })}
                      />
                    )}
                  </th>
                  <td>
                    <span className="hilfe">{z.schluessel}</span>
                  </td>
                  <td className="zahl">
                    {hier === null ? (
                      z.reihenfolge
                    ) : (
                      <input
                        className="feld__eingabe feld__eingabe--schmal"
                        aria-label={`Reihenfolge von ${z.bezeichnung}`}
                        inputMode="numeric"
                        value={hier.reihenfolge}
                        aria-invalid={bearbeitungReihenfolge === null}
                        onChange={(e) => setzeBearbeitung({ ...hier, reihenfolge: e.target.value })}
                      />
                    )}
                  </td>
                  <td>
                    {z.aktiv ? (
                      <span className="marke marke--gut">in Gebrauch</span>
                    ) : (
                      <span className="marke marke--inaktiv">außer Gebrauch</span>
                    )}
                  </td>
                  {darfKonfigurieren && (
                    <td>
                      {hier === null ? (
                        <div className="knopfreihe">
                          <button
                            type="button"
                            className="knopf knopf--klein"
                            disabled={laeuft}
                            onClick={() => {
                              setzeNeu(null);
                              setzeBearbeitung({
                                schluessel: z.schluessel,
                                bezeichnung: z.bezeichnung,
                                reihenfolge: String(z.reihenfolge),
                              });
                            }}
                          >
                            Ändern
                          </button>
                          <button
                            type="button"
                            className="knopf knopf--klein"
                            disabled={laeuft}
                            onClick={() =>
                              void handgriff(
                                () =>
                                  artAendern(z.schluessel, {
                                    bezeichnung: z.bezeichnung,
                                    reihenfolge: z.reihenfolge,
                                    aktiv: !z.aktiv,
                                  }),
                                z.aktiv
                                  ? `„${z.bezeichnung}" ist außer Gebrauch und wird nicht mehr angeboten.`
                                  : `„${z.bezeichnung}" ist wieder in Gebrauch.`,
                              )
                            }
                          >
                            {z.aktiv ? 'Außer Gebrauch nehmen' : 'Wieder in Gebrauch nehmen'}
                          </button>
                        </div>
                      ) : (
                        <div className="knopfreihe">
                          <button type="button" className="knopf knopf--klein" onClick={() => setzeBearbeitung(null)}>
                            Abbrechen
                          </button>
                          <button
                            type="button"
                            className="knopf knopf--klein"
                            disabled={laeuft || hier.bezeichnung.trim() === '' || bearbeitungReihenfolge === null}
                            onClick={() => {
                              if (bearbeitungReihenfolge === null) return;
                              const wort = hier.bezeichnung.trim();
                              void handgriff(async () => {
                                await artAendern(z.schluessel, {
                                  bezeichnung: wort,
                                  reihenfolge: bearbeitungReihenfolge,
                                  aktiv: z.aktiv,
                                });
                                setzeBearbeitung(null);
                              }, `„${wort}" gespeichert.`);
                            }}
                          >
                            Übernehmen
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {darfKonfigurieren && neu === null && (
        <div className="knopfreihe">
          <button
            type="button"
            className="knopf knopf--klein"
            onClick={() => {
              setzeBearbeitung(null);
              setzeNeu({ bezeichnung: '', schluessel: '', schluesselGetippt: false });
            }}
          >
            Art anlegen
          </button>
        </div>
      )}

      {darfKonfigurieren && neu !== null && (
        <>
          <div className="feldreihe">
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="kostentraegerart-bezeichnung">
                Bezeichnung
              </label>
              <input
                id="kostentraegerart-bezeichnung"
                className="feld__eingabe"
                value={neu.bezeichnung}
                maxLength={80}
                onChange={(e) =>
                  setzeNeu({
                    ...neu,
                    bezeichnung: e.target.value,
                    // E-74 (CRM-163): Der Schlüssel folgt, bis jemand ihn selbst ändert.
                    schluessel: neu.schluesselGetippt ? neu.schluessel : schluesselAus(e.target.value),
                  })
                }
              />
            </div>
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="kostentraegerart-schluessel">
                Schlüssel
              </label>
              <input
                id="kostentraegerart-schluessel"
                className="feld__eingabe feld__eingabe--schmal"
                value={neu.schluessel}
                maxLength={40}
                aria-invalid={neuSchluesselFalsch}
                aria-describedby="kostentraegerart-schluessel-hilfe"
                onChange={(e) => setzeNeu({ ...neu, schluessel: e.target.value, schluesselGetippt: true })}
              />
            </div>
          </div>
          <p className="feld__hilfe" id="kostentraegerart-schluessel-hilfe">
            {neuSchluesselFalsch
              ? 'Der Schlüssel beginnt mit einem Kleinbuchstaben und enthält nur Kleinbuchstaben, Ziffern und Unterstriche, 2 bis 40 Zeichen.'
              : 'Der Schlüssel wird aus der Bezeichnung vorgeschlagen und bleibt danach, wie er ist. Die Bezeichnung lässt sich jederzeit ändern.'}
          </p>
          <div className="knopfreihe">
            <button type="button" className="knopf" onClick={() => setzeNeu(null)}>
              Abbrechen
            </button>
            <button
              type="button"
              className="knopf knopf--haupt"
              disabled={laeuft || neu.bezeichnung.trim() === '' || !SCHLUESSELMUSTER.test(neu.schluessel)}
              onClick={() => {
                const wort = neu.bezeichnung.trim();
                void handgriff(async () => {
                  await artAnlegen({
                    schluessel: neu.schluessel,
                    bezeichnung: wort,
                    // Ans Ende, in Zehnerschritten — so passt später eine Art dazwischen.
                    reihenfolge: Math.min(9999, Math.max(0, ...zeilen.map((z) => z.reihenfolge)) + 10),
                    aktiv: true,
                  });
                  setzeNeu(null);
                }, `„${wort}" steht in der Liste.`);
              }}
            >
              Anlegen
            </button>
          </div>
        </>
      )}
    </Thema>
  );
}
